import { useMemo } from 'react';
import { usePreferences } from './contexts/PreferencesContext';

type TranslationKey = keyof typeof translations['pt-BR'];

type Replacements = Record<string, string | number>;

const translations = {
  'pt-BR': {
    'app.name': 'StockFlow',
    'app.version': 'v1.1',
    'nav.scanner': 'Scanner',
    'nav.picking': 'Separação',
    'nav.inventory': 'Inventário',
    'nav.history': 'Histórico',
    'nav.scan': 'Ler',
    'nav.stock': 'Estoque',
    'preferences.theme': 'Tema',
    'preferences.language': 'Idioma',
    'preferences.theme.light': 'Claro',
    'preferences.theme.dark': 'Escuro',
    'preferences.theme.high-contrast': 'Alto contraste',
    'preferences.language.pt-BR': 'Português (BR)',
    'preferences.language.en': 'Inglês',

    'stock.title': 'Estoque Atual',
    'stock.search.placeholder': 'Buscar produto, SKU ou cor...',
    'stock.loading': 'Carregando estoque...',
    'stock.empty': 'Nenhum produto encontrado.',
    'stock.table.color': 'Cor \\ Tamanho',
    'stock.total': 'Total',
    'stock.table.product': 'Produto',
    'stock.table.variant': 'Variação',
    'stock.table.stock': 'Estoque',
    'stock.table.actions': 'Ações',
    'stock.editName': 'Editar nome',
    'stock.editStock': 'Editar estoque',
    'stock.editDetails': 'Editar detalhes',
    'stock.editDetailsTitle': 'Detalhes do produto',
    'stock.renameLabel': 'Atualizar nome do modelo',
    'stock.renamePlaceholder': 'Ex: Sandália tiras',
    'stock.save': 'Salvar',
    'stock.saving': 'Salvando...',
    'stock.deleting': 'Desativando...',
    'stock.cancel': 'Cancelar',
    'stock.renameError': 'Não foi possível salvar o nome. Tente novamente.',
    'stock.adjust.success': 'Estoque atualizado para {{value}}.',
    'stock.adjust.error': 'Falha ao ajustar estoque.',
    'stock.unknownProduct': 'Produto desconhecido',
    'stock.deactivateProduct': 'Desativar produto',
    'stock.deactivateVariant': 'Desativar variação',
    'stock.confirmDeleteProduct': 'Deseja desativar o produto "{{name}}"? Ele ficará oculto, mas o histórico será mantido.',
    'stock.confirmDeleteVariant': 'Deseja desativar a variação "{{name}}"? Ela ficará oculta, mas o histórico será mantido.',
    'stock.deleteSuccessProduct': 'Produto desativado com sucesso.',
    'stock.deleteErrorProduct': 'Não foi possível desativar o produto.',
    'stock.deleteSuccessVariant': 'Variação desativada com sucesso.',
    'stock.deleteErrorVariant': 'Não foi possível desativar a variação.',
    'stock.updateProductSuccess': 'Produto atualizado com sucesso.',
    'stock.updateProductError': 'Não foi possível atualizar o produto.',
    'stock.updateVariantSuccess': 'Variação atualizada com sucesso.',
    'stock.updateVariantError': 'Não foi possível atualizar a variação.',
    'stock.variantActionsTitle': 'Ações por variação',
    'stock.variantSortLabel': 'Ordenação',
    'stock.variantSortManual': 'Manual',
    'stock.variantSortSkuAsc': 'SKU A→Z',
    'stock.variantSortSkuDesc': 'SKU Z→A',
    'stock.variantSortColorSizeAsc': 'Cor/Tamanho A→Z',
    'stock.variantSortColorSizeDesc': 'Cor/Tamanho Z→A',
    'stock.reorderUp': 'Subir',
    'stock.reorderDown': 'Descer',
    'stock.reorderError': 'Não foi possível reordenar o produto.',
    'stock.reorderVariantError': 'Não foi possível reordenar a variação.',
    'stock.productIconAlt': 'Ícone do produto {{name}}',
    'stock.productIconPreviewAlt': 'Pré-visualização do ícone para {{name}}',
    'stock.fields.name': 'Nome',
    'stock.fields.baseSku': 'SKU base',
    'stock.fields.description': 'Descrição',
    'stock.fields.displayColor': 'Cor de destaque',
    'stock.fields.iconUrl': 'URL do ícone',
    'stock.fields.iconUrlPlaceholder': 'https://.../icone.png',
    'stock.fields.iconPreview': 'Pré-visualização',
    'stock.fields.active': 'Ativo',
    'stock.fields.sku': 'SKU',
    'stock.fields.color': 'Cor',
    'stock.fields.size': 'Tamanho',
    'stock.validation.requiredFields': 'Nome e SKU base são obrigatórios.',
    'stock.validation.baseSkuExists': 'Este SKU base já existe.',
    'stock.validation.skuExists': 'Este SKU já existe.',
    'stock.validation.skuRequired': 'O SKU é obrigatório.',
    'stock.alertSettings.subtitle': 'Configure limiares mínimos para receber alertas visuais.',
    'stock.alertSettings.globalTitle': 'Limiar global',
    'stock.alertSettings.globalHint': 'Aplicado para todos os produtos sem valor específico.',
    'stock.alertSettings.globalLabel': 'Quantidade mínima',
    'stock.alertSettings.currentThreshold': 'Usando mínimo: {{value}}',
    'stock.alertSettings.bufferLabel': 'Buffer +{{value}}',
    'stock.alertSettings.bufferHint': 'Valores entre o limiar e o buffer exibem atenção.',
    'stock.alertSettings.categoryTitle': 'Limiar por categoria',
    'stock.alertSettings.categoryHint': 'Use o código base/REF para aplicar a todas as variações.',
    'stock.alertSettings.detectedCategories': '{{count}} categorias detectadas',
    'stock.alertSettings.categoryPlaceholder': 'Ex: RASTEIRA-TIR',
    'stock.alertSettings.addCategory': 'Adicionar',
    'stock.alertSettings.noCategories': 'Nenhuma categoria personalizada ainda.',
    'stock.alertSettings.thresholdValue': 'Limiar {{value}}',
    'stock.alertSettings.removeCategory': 'Remover {{category}}',
    'stock.alertSettings.removeLabel': 'Remover',
    'stock.alertSettings.legendTitle': 'Legenda de status',
    'stock.alertSettings.legend.ok': 'OK',
    'stock.alertSettings.legend.attention': 'Atenção',
    'stock.alertSettings.legend.critical': 'Crítico',
    'stock.alertSettings.legendCritical': '≤ {{value}}',
    'stock.alertSettings.legendAttention': 'até {{value}}',
    'stock.alertSettings.legendOk': '> {{value}}',

    'movements.entry': 'Entrada',
    'movements.exit': 'Saída',
    'movements.manual.placeholder': 'Digite SKU ou escaneie...',
    'movements.toast.newItem': 'Novo item detectado! Registrando {{sku}}...',
    'movements.toast.scanned': 'Item lido: {{sku}}',
    'movements.toast.registerError': 'Falha ao registrar novo SKU: {{sku}}',
    'movements.toast.scanError': 'Erro ao processar leitura',
    'movements.toast.saved': 'Movimentação salva com sucesso!',
    'movements.toast.saveError': 'Falha ao salvar.',
    'movements.ready': 'Pronto para escanear itens',
    'movements.confirm': 'CONFIRMAR {{mode}} ({{count}} ITENS)',
    'movements.saving': 'Salvando...',
    'movements.cart.badge': '{{color}} - {{size}}',

    'reports.title': 'Movimentações Recentes',
    'reports.subtitle': 'Exibindo últimas atividades do banco de dados',
    'reports.loading': 'Carregando histórico...',
    'reports.empty': 'Nenhuma movimentação registrada ainda.',

    'separation.title': 'Picking & Separação',
    'separation.scan.hint': 'Escaneie itens da sua lista de separação.',
    'separation.analysis.hint': 'Revise a disponibilidade antes da baixa.',
    'separation.manual.placeholder': 'Escaneie ou digite o SKU...',
    'separation.empty': 'Escaneie itens para montar a lista',
    'separation.analyze': 'ANALISAR ({{count}} ITENS)',
    'separation.analyzing': 'Analisando...',
    'separation.report.title': 'Relatório de Comparação',
    'separation.report.edit': '← Editar lista',
    'separation.report.requested': 'Solicitado',
    'separation.report.inStock': 'Em estoque',
    'separation.report.available': 'DISPONÍVEL',
    'separation.report.missing': 'FALTANDO',
    'separation.confirm': 'CONFIRMAR E BAIXAR ESTOQUE',
    'separation.processing': 'Processando...',
    'separation.notice': 'Somente itens totalmente disponíveis serão baixados.',
    'separation.success.title': 'Separação Concluída!',
    'separation.success.copy': 'Estoque foi deduzido para os itens disponíveis.',
    'separation.success.cta': 'Iniciar nova separação',

    'toast.availableNone': 'Nenhum item disponível para baixa.',
    'toast.checkedOut': 'Baixados {{count}} itens.',
  },
  en: {
    'app.name': 'StockFlow',
    'app.version': 'v1.1',
    'nav.scanner': 'Scanner',
    'nav.picking': 'Picking',
    'nav.inventory': 'Inventory',
    'nav.history': 'History',
    'nav.scan': 'Scan',
    'nav.stock': 'Stock',
    'preferences.theme': 'Theme',
    'preferences.language': 'Language',
    'preferences.theme.light': 'Light',
    'preferences.theme.dark': 'Dark',
    'preferences.theme.high-contrast': 'High contrast',
    'preferences.language.pt-BR': 'Portuguese (BR)',
    'preferences.language.en': 'English',

    'stock.title': 'Current Stock',
    'stock.search.placeholder': 'Search product, SKU or color...',
    'stock.loading': 'Loading inventory...',
    'stock.empty': 'No products found.',
    'stock.table.color': 'Color \\ Size',
    'stock.total': 'Total',
    'stock.table.product': 'Product',
    'stock.table.variant': 'Variant',
    'stock.table.stock': 'Stock',
    'stock.table.actions': 'Actions',
    'stock.editName': 'Edit name',
    'stock.editStock': 'Edit stock',
    'stock.editDetails': 'Edit details',
    'stock.editDetailsTitle': 'Product details',
    'stock.renameLabel': 'Update model name',
    'stock.renamePlaceholder': 'e.g. Strappy sandal',
    'stock.save': 'Save',
    'stock.saving': 'Saving...',
    'stock.deleting': 'Deactivating...',
    'stock.cancel': 'Cancel',
    'stock.renameError': 'Could not save the name. Please try again.',
    'stock.adjust.success': 'Stock updated to {{value}}.',
    'stock.adjust.error': 'Failed to adjust stock.',
    'stock.unknownProduct': 'Unknown product',
    'stock.deactivateProduct': 'Deactivate product',
    'stock.deactivateVariant': 'Deactivate variant',
    'stock.confirmDeleteProduct': 'Deactivate the product "{{name}}"? It will be hidden, but history remains.',
    'stock.confirmDeleteVariant': 'Deactivate the variant "{{name}}"? It will be hidden, but history remains.',
    'stock.deleteSuccessProduct': 'Product deactivated successfully.',
    'stock.deleteErrorProduct': 'Could not deactivate the product.',
    'stock.deleteSuccessVariant': 'Variant deactivated successfully.',
    'stock.deleteErrorVariant': 'Could not deactivate the variant.',
    'stock.updateProductSuccess': 'Product updated successfully.',
    'stock.updateProductError': 'Could not update the product.',
    'stock.updateVariantSuccess': 'Variant updated successfully.',
    'stock.updateVariantError': 'Could not update the variant.',
    'stock.variantActionsTitle': 'Variant actions',
    'stock.variantSortLabel': 'Sort',
    'stock.variantSortManual': 'Manual',
    'stock.variantSortSkuAsc': 'SKU A→Z',
    'stock.variantSortSkuDesc': 'SKU Z→A',
    'stock.variantSortColorSizeAsc': 'Color/Size A→Z',
    'stock.variantSortColorSizeDesc': 'Color/Size Z→A',
    'stock.reorderUp': 'Move up',
    'stock.reorderDown': 'Move down',
    'stock.reorderError': 'Could not reorder the product.',
    'stock.reorderVariantError': 'Could not reorder the variant.',
    'stock.productIconAlt': 'Product icon for {{name}}',
    'stock.productIconPreviewAlt': 'Icon preview for {{name}}',
    'stock.fields.name': 'Name',
    'stock.fields.baseSku': 'Base SKU',
    'stock.fields.description': 'Description',
    'stock.fields.displayColor': 'Display color',
    'stock.fields.iconUrl': 'Icon URL',
    'stock.fields.iconUrlPlaceholder': 'https://.../icon.png',
    'stock.fields.iconPreview': 'Preview',
    'stock.fields.active': 'Active',
    'stock.fields.sku': 'SKU',
    'stock.fields.color': 'Color',
    'stock.fields.size': 'Size',
    'stock.validation.requiredFields': 'Name and base SKU are required.',
    'stock.validation.baseSkuExists': 'This base SKU already exists.',
    'stock.validation.skuExists': 'This SKU already exists.',
    'stock.validation.skuRequired': 'SKU is required.',
    'stock.alertSettings.subtitle': 'Configure minimum thresholds to trigger visual alerts.',
    'stock.alertSettings.globalTitle': 'Global threshold',
    'stock.alertSettings.globalHint': 'Applied to all products without a specific value.',
    'stock.alertSettings.globalLabel': 'Minimum quantity',
    'stock.alertSettings.currentThreshold': 'Using minimum: {{value}}',
    'stock.alertSettings.bufferLabel': 'Buffer +{{value}}',
    'stock.alertSettings.bufferHint': 'Values between the threshold and buffer show attention state.',
    'stock.alertSettings.categoryTitle': 'Category threshold',
    'stock.alertSettings.categoryHint': 'Use the base code/REF to apply to all variations.',
    'stock.alertSettings.detectedCategories': '{{count}} categories detected',
    'stock.alertSettings.categoryPlaceholder': 'e.g. SANDAL-STR',
    'stock.alertSettings.addCategory': 'Add',
    'stock.alertSettings.noCategories': 'No custom categories yet.',
    'stock.alertSettings.thresholdValue': 'Threshold {{value}}',
    'stock.alertSettings.removeCategory': 'Remove {{category}}',
    'stock.alertSettings.removeLabel': 'Remove',
    'stock.alertSettings.legendTitle': 'Status legend',
    'stock.alertSettings.legend.ok': 'OK',
    'stock.alertSettings.legend.attention': 'Attention',
    'stock.alertSettings.legend.critical': 'Critical',
    'stock.alertSettings.legendCritical': '≤ {{value}}',
    'stock.alertSettings.legendAttention': 'up to {{value}}',
    'stock.alertSettings.legendOk': '> {{value}}',

    'movements.entry': 'Entry',
    'movements.exit': 'Exit',
    'movements.manual.placeholder': 'Type SKU or Scan...',
    'movements.toast.newItem': 'New item detected! Registering {{sku}}...',
    'movements.toast.scanned': 'Scanned: {{sku}}',
    'movements.toast.registerError': 'Failed to register new SKU: {{sku}}',
    'movements.toast.scanError': 'Error processing scan',
    'movements.toast.saved': 'Movement saved successfully!',
    'movements.toast.saveError': 'Failed to save.',
    'movements.ready': 'Ready to scan items',
    'movements.confirm': 'CONFIRM {{mode}} ({{count}} ITEMS)',
    'movements.saving': 'Saving...',
    'movements.cart.badge': '{{color}} - {{size}}',

    'reports.title': 'Recent Movements',
    'reports.subtitle': 'Showing latest activity from database',
    'reports.loading': 'Loading history...',
    'reports.empty': 'No movements recorded yet.',

    'separation.title': 'Picking & Separation',
    'separation.scan.hint': 'Scan items from your picking list.',
    'separation.analysis.hint': 'Review stock availability before checkout.',
    'separation.manual.placeholder': 'Scan or type SKU...',
    'separation.empty': 'Scan items to build list',
    'separation.analyze': 'ANALYZE ({{count}} ITEMS)',
    'separation.analyzing': 'Analyzing...',
    'separation.report.title': 'Comparison Report',
    'separation.report.edit': '← Edit List',
    'separation.report.requested': 'Requested',
    'separation.report.inStock': 'In Stock',
    'separation.report.available': 'AVAILABLE',
    'separation.report.missing': 'MISSING',
    'separation.confirm': 'CONFIRM & DEDUCT STOCK',
    'separation.processing': 'Processing...',
    'separation.notice': 'Only fully available items will be deducted.',
    'separation.success.title': 'Separation Complete!',
    'separation.success.copy': 'Stock has been deducted for available items.',
    'separation.success.cta': 'Start New Picking',

    'toast.availableNone': 'No items available to checkout.',
    'toast.checkedOut': 'Checked out {{count}} items.',
  },
};

const formatWithReplacements = (template: string, replacements?: Replacements) => {
  if (!replacements) return template;
  return Object.entries(replacements).reduce((acc, [key, value]) => {
    return acc.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
  }, template);
};

export const useI18n = () => {
  const { language } = usePreferences();

  const t = useMemo(() => {
    return (key: TranslationKey, replacements?: Replacements) => {
      const locale = translations[language] ?? translations['pt-BR'];
      const fallback = translations['pt-BR'];
      const template = locale[key] ?? fallback[key] ?? key;
      return formatWithReplacements(template, replacements);
    };
  }, [language]);

  return { t, language };
};

export type { TranslationKey };
